import React, { useState, useEffect } from 'react';
import { blogService, mediaService } from '../../services/api';
import HtmlEditor from './HtmlEditor';
import { 
  Plus, Search, Filter, Edit2, Trash2, Eye, 
  Image as ImageIcon, Tag, Globe, Settings, 
  CheckCircle, Clock, Archive, Star,
  X, Save, Loader2, ChevronRight
} from 'lucide-react';

function BlogManagement() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    status: 'draft',
    is_featured: false,
    featured_image: '',
    tags: [],
    meta_title: '',
    meta_description: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState('content'); // content, settings, seo

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const data = await blogService.index();
      setBlogs(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSlugGen = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug === handleSlugGen(prev.title) || !prev.slug ? handleSlugGen(title) : prev.slug
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const res = await mediaService.upload(file, 'blogs');
      setFormData(prev => ({ ...prev, featured_image: res.url }));
    } catch (error) {
      alert('Failed to upload image');
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingBlog) {
        await blogService.update(editingBlog.id, formData);
      } else {
        await blogService.store(formData);
      }
      setShowModal(false);
      resetForm();
      loadBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog post. Check console for details.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      status: 'draft',
      is_featured: false,
      featured_image: '',
      tags: [],
      meta_title: '',
      meta_description: '',
    });
    setTagInput('');
    setActiveTab('content');
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title || '',
      slug: blog.slug || '',
      content: blog.content || '',
      excerpt: blog.excerpt || '',
      status: blog.status || 'draft',
      is_featured: !!blog.is_featured,
      featured_image: blog.featured_image || '',
      tags: Array.isArray(blog.tags) ? blog.tags : [],
      meta_title: blog.meta_title || '',
      meta_description: blog.meta_description || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await blogService.destroy(id);
        loadBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          blog.slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || blog.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: blogs.length,
    published: blogs.filter(b => b.status === 'published').length,
    drafts: blogs.filter(b => b.status === 'draft').length,
    featured: blogs.filter(b => b.is_featured).length,
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Blog Management</h2>
          <p className="text-slate-400 text-sm">Create and manage your articles, news, and updates.</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl transition font-medium shadow-lg shadow-red-900/20"
        >
          <Plus size={18} /> Add New Post
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Total Posts</p>
          <p className="text-2xl font-bold text-slate-100">{stats.total}</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-red-500/80 text-xs font-semibold uppercase tracking-wider">Published</p>
          <p className="text-2xl font-bold text-slate-100">{stats.published}</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-amber-500/80 text-xs font-semibold uppercase tracking-wider">Drafts</p>
          <p className="text-2xl font-bold text-slate-100">{stats.drafts}</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-purple-500/80 text-xs font-semibold uppercase tracking-wider">Featured</p>
          <p className="text-2xl font-bold text-slate-100">{stats.featured}</p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input
            type="text"
            placeholder="Search by title or slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="text-slate-500" size={18} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded-xl py-2.5 px-4 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Blog List Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/50 border-b border-slate-800">
              <tr>
                <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Post</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Views</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Created At</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredBlogs.map((blog) => (
                <tr key={blog.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-800 overflow-hidden shrink-0 border border-slate-700">
                        {blog.featured_image ? (
                          <img src={blog.featured_image} alt={blog.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-600">
                            <ImageIcon size={20} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-100 truncate">{blog.title}</p>
                          {blog.is_featured && (
                            <Star size={12} className="text-amber-400 fill-amber-400" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate">/{blog.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      blog.status === 'published' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                      blog.status === 'draft' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                      'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                    }`}>
                      {blog.status === 'published' ? <CheckCircle size={10} /> : 
                       blog.status === 'draft' ? <Clock size={10} /> : <Archive size={10} />}
                      <span className="capitalize">{blog.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <Eye size={14} className="text-slate-500" />
                      {blog.views || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {new Date(blog.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(blog)}
                        className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition"
                        title="Edit Post"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(blog.id)}
                        className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition"
                        title="Delete Post"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredBlogs.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    No blog posts found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => !submitting && setShowModal(false)} />
          <div className="relative bg-slate-900 border border-slate-800 w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/50 sticky top-0 z-10">
              <div>
                <h3 className="text-xl font-bold text-slate-100">
                  {editingBlog ? 'Edit Blog Post' : 'Create New Post'}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {editingBlog ? `ID: ${editingBlog.id}` : 'Draft will be saved automatically'}
                </p>
              </div>
              <button 
                onClick={() => !submitting && setShowModal(false)}
                className="p-2 text-slate-500 hover:text-slate-300 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Tabs */}
            <div className="px-6 border-b border-slate-800 flex items-center gap-6 bg-slate-900/30">
              <button
                onClick={() => setActiveTab('content')}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'content' ? 'border-red-500 text-red-400' : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                Content
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'settings' ? 'border-red-500 text-red-400' : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                Post Settings
              </button>
              <button
                onClick={() => setActiveTab('seo')}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'seo' ? 'border-red-500 text-red-400' : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                SEO & Metadata
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              {activeTab === 'content' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2 space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-1.5">Post Title</label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={handleTitleChange}
                          placeholder="e.g. The Future of FMCG in 2026"
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-1.5">Slug (URL)</label>
                        <div className="flex items-center">
                          <span className="bg-slate-800 border border-r-0 border-slate-800 px-4 py-3 rounded-l-xl text-slate-500 text-sm italic">ortus.com/blog/</span>
                          <input
                            type="text"
                            required
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            className="flex-1 bg-slate-950 border border-slate-800 rounded-r-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <label className="block text-sm font-semibold text-slate-300 mb-1.5">Featured Image</label>
                      <div className="relative aspect-video rounded-xl bg-slate-950 border-2 border-dashed border-slate-800 flex flex-col items-center justify-center overflow-hidden group hover:border-red-500/50 transition-colors cursor-pointer">
                        {formData.featured_image ? (
                          <>
                            <img src={formData.featured_image} alt="Preview" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                              <p className="text-white text-xs font-bold">Change Image</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <ImageIcon className="text-slate-600 mb-2" size={32} />
                            <p className="text-slate-500 text-xs font-medium">Click to upload image</p>
                          </>
                        )}
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleImageUpload}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Post Content</label>
                    <HtmlEditor 
                      value={formData.content} 
                      onChange={(val) => setFormData({ ...formData, content: val })} 
                      editorKey={editingBlog ? `edit-${editingBlog.id}` : 'new-post'}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-1.5">Post Excerpt (Summary)</label>
                        <textarea
                          rows={4}
                          value={formData.excerpt}
                          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                          placeholder="Brief summary shown in blog list cards..."
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors resize-none"
                        />
                        <p className="text-[10px] text-slate-500 mt-2">Recommended: 150-160 characters for best display.</p>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-red-500/10 rounded-lg text-red-400">
                            <Star size={20} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-100">Featured Post</p>
                            <p className="text-[10px] text-slate-500">Highlight this post on the home page</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={formData.is_featured} 
                            onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                            className="sr-only peer" 
                          />
                          <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                        </label>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-1.5">Status</label>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { id: 'draft', label: 'Draft', icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
                            { id: 'published', label: 'Published', icon: CheckCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
                            { id: 'archived', label: 'Archived', icon: Archive, color: 'text-slate-400', bg: 'bg-slate-500/10' },
                          ].map(opt => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => setFormData({ ...formData, status: opt.id })}
                              className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                                formData.status === opt.id 
                                  ? `border-${opt.id === 'draft' ? 'amber' : opt.id === 'published' ? 'red' : 'slate'}-500/50 ${opt.bg}`
                                  : 'border-slate-800 hover:border-slate-700 bg-slate-950/50'
                              }`}
                            >
                              <opt.icon size={20} className={formData.status === opt.id ? opt.color : 'text-slate-500'} />
                              <span className={`text-[10px] font-bold mt-2 ${formData.status === opt.id ? 'text-slate-100' : 'text-slate-500'}`}>
                                {opt.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-2">
                          <Tag size={14} /> Tags
                        </label>
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleAddTag}
                          placeholder="Type and press Enter..."
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                        />
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.tags.map(tag => (
                            <span 
                              key={tag} 
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20 text-xs font-bold"
                            >
                              {tag}
                              <button 
                                onClick={() => removeTag(tag)}
                                className="hover:text-white transition"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'seo' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="bg-slate-950 border border-slate-800 p-6 rounded-2xl space-y-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400">
                        <Globe size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-100">Google Search Preview</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">SERP Visualization</p>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                      <p className="text-[#1a0dab] text-xl font-medium truncate mb-1">
                        {formData.meta_title || formData.title || 'Post Title - Ortus FMCG'}
                      </p>
                      <p className="text-[#006621] text-sm mb-1 truncate">
                        https://ortus.com/blog/{formData.slug || 'slug'}
                      </p>
                      <p className="text-[#545454] text-sm line-clamp-2">
                        {formData.meta_description || formData.excerpt || 'Add a meta description to see how this post will look in Google search results.'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-1.5">Meta Title</label>
                      <input
                        type="text"
                        value={formData.meta_title}
                        onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                        placeholder="Post title for SEO"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                      />
                      <p className="text-[10px] text-slate-500 mt-2 flex justify-between">
                        <span>Optimal: 50-60 characters</span>
                        <span>{formData.meta_title.length} / 60</span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-1.5">Meta Description</label>
                      <textarea
                        rows={3}
                        value={formData.meta_description}
                        onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                        placeholder="Search engine summary..."
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors resize-none"
                      />
                      <p className="text-[10px] text-slate-500 mt-2 flex justify-between">
                        <span>Optimal: 150-160 characters</span>
                        <span>{formData.meta_description.length} / 160</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </form>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between bg-slate-900/50 sticky bottom-0 z-10">
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
                  <span className={`w-2 h-2 rounded-full ${formData.content.length > 100 ? 'bg-red-500' : 'bg-slate-700'}`} />
                  {formData.content.length} characters written
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => !submitting && setShowModal(false)}
                  disabled={submitting}
                  className="px-6 py-2.5 rounded-xl border border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition font-medium disabled:opacity-50"
                >
                  Discard Changes
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !formData.title || !formData.content}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-2.5 rounded-xl transition font-bold shadow-lg shadow-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingBlog ? 'Update Post' : 'Publish Now'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogManagement;

